import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Alert,
    Box,
    Card,
    CardActionArea,
    Container,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {
    AcUnit,
    Agriculture,
    Brush,
    CameraAltOutlined,
    CarRepair,
    ChairAlt,
    CleaningServices,
    Code,
    Computer,
    Construction,
    ContentCut,
    DesignServices,
    ElectricalServices,
    FormatPaint,
    Handyman,
    HomeRepairService,
    HomeWork,
    Kitchen,
    LocalShipping,
    MicNone,
    MusicNote,
    PhoneAndroid,
    PhotoCamera,
    Plumbing,
    Restaurant,
    Roofing,
    School,
    Search,
    Security,
    Spa,
    Translate,
    Videocam,
} from '@mui/icons-material'

import api from '../services/api'
import fallbackCategories from '../serviceProsFallback'
import ServiceRequestFlow from '../components/ServiceRequestFlow'

const iconMap = {
    plumbing: Plumbing,
    electrical: ElectricalServices,
    cleaning: CleaningServices,
    painting: FormatPaint,
    furniture: ChairAlt,
    appliance: Kitchen,
    handyman: Handyman,
    construction: Construction,
    remodeling: HomeWork,
    roofing: Roofing,
    air_conditioning: AcUnit,
    generator: ElectricalServices,
    auto_repair: CarRepair,
    phone_repair: PhoneAndroid,
    computer_repair: Computer,
    moving: LocalShipping,
    catering: Restaurant,
    photography: PhotoCamera,
    videography: Videocam,
    tailoring: ContentCut,
    beauty: Spa,
    makeup: Brush,
    event_decoration: Brush,
    entertainment: MusicNote,
    tutoring: School,
    translation: Translate,
    graphic_design: DesignServices,
    web_development: Code,
    farming: Agriculture,
    security: Security,
}

const ServicePros = () => {
    const [categories, setCategories] = useState(fallbackCategories)
    const [query, setQuery] = useState('')
    const [usingFallback, setUsingFallback] = useState(true)
    const [error, setError] = useState('')
    const [voiceActive, setVoiceActive] = useState(false)
    const [selectedCategory, setSelectedCategory] = useState(null)
    const cameraInputRef = useRef(null)

    const loadCategories = useCallback(async () => {
        try {
            const response = await api.get('/service-pros/categories')
            const live = Array.isArray(response.data?.data) ? response.data.data : []
            if (live.length) { setCategories(live); setUsingFallback(false); setError('') }
        } catch (requestError) {
            setError('Showing saved categories while MojoPayr reconnects.')
        }
    }, [])

    useEffect(() => {
        loadCategories()
        const timer = window.setInterval(loadCategories, 15000)
        return () => window.clearInterval(timer)
    }, [loadCategories])

    const visibleCategories = useMemo(() => {
        const needle = query.trim().toLowerCase()
        return needle ? categories.filter((item) => `${item.name} ${item.description || ''}`.toLowerCase().includes(needle)) : categories
    }, [categories, query])

    const startVoiceSearch = () => {
        const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition
        if (!Recognition) {
            setError('Voice search is not supported by this browser. You can still type your search.')
            return
        }

        const recognition = new Recognition()
        recognition.lang = 'en-US'
        recognition.interimResults = false
        recognition.maxAlternatives = 1
        recognition.onstart = () => {
            setError('')
            setVoiceActive(true)
        }
        recognition.onresult = (event) => setQuery(event.results?.[0]?.[0]?.transcript || '')
        recognition.onerror = () => setError('Voice search could not hear you. Please try again or type the service.')
        recognition.onend = () => setVoiceActive(false)
        recognition.start()
    }

    const imageSelected = (event) => {
        const file = event.target.files?.[0]
        if (!file) return
        const fileSearch = file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim()
        if (fileSearch && !/^(image|img)\d*$/i.test(fileSearch)) {
            setQuery(fileSearch)
        } else {
            setError('Photo added. Type or select the service that best matches the image.')
        }
        event.target.value = ''
    }

    const content = useMemo(() => {
        if (visibleCategories.length === 0) {
            return (
                <Box sx={{ minHeight: 320, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
                    <Stack spacing={1.2} alignItems="center">
                        <Search sx={{ color: '#FF8A00', fontSize: 54 }} />
                        <Typography variant="h6" fontWeight={900}>No services found</Typography>
                        <Typography color="text.secondary">Try another search or clear the search box.</Typography>
                    </Stack>
                </Box>
            )
        }
        return (
            <Grid container spacing={2}>
                {visibleCategories.map((category) => {
                    const CategoryIcon = iconMap[category.icon_key] || HomeRepairService
                    return (
                        <Grid item xs={6} sm={4} md={3} lg={2.4} key={category.id}>
                            <Card sx={{ height: '100%', borderRadius: 4, bgcolor: 'rgba(26,19,51,.96)', border: '1px solid rgba(255,255,255,.07)' }}>
                                <CardActionArea onClick={() => setSelectedCategory(category)} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ width: '100%', aspectRatio: '1.25', display: 'grid', placeItems: 'center', bgcolor: '#241A47', overflow: 'hidden', position: 'relative' }}>
                                        <CategoryIcon sx={{ color: '#FF8A00', fontSize: 44 }} />
                                        {(category.image || category.image_url) && <Box component="img" src={category.image || category.image_url} alt="" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} onError={(event) => { event.currentTarget.style.display = 'none' }} />}
                                    </Box>
                                    <Typography sx={{ p: 1.5, minHeight: 58, display: 'grid', placeItems: 'center', textAlign: 'center', fontWeight: 800, fontSize: 14 }}>{category.name}</Typography>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    )
                })}
            </Grid>
        )
    }, [visibleCategories])

    return (
        <Box sx={{ minHeight: '100vh', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="xl">
                <Stack spacing={1} sx={{ mb: 3 }}>
                    <Typography variant="h3" fontWeight={950}>Service Pros</Typography>
                    <Typography color="text.secondary" fontSize={17}>Find skilled professionals for home, business, events, repairs, and more.</Typography>
                </Stack>

                <TextField
                    fullWidth
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="What service do you need?"
                    sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 4, bgcolor: 'rgba(26,19,51,.96)' } }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Search sx={{ color: '#FF8A00' }} /></InputAdornment>,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton aria-label="Voice search" onClick={startVoiceSearch} sx={{ color: voiceActive ? '#22c55e' : '#FF8A00' }}><MicNone /></IconButton>
                                <IconButton aria-label="Image search" onClick={() => cameraInputRef.current?.click()} sx={{ color: '#FF8A00' }}><CameraAltOutlined /></IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={imageSelected} />

                {usingFallback && error && <Alert severity="info" sx={{ mb: 2 }}>{error}</Alert>}
                {selectedCategory && <ServiceRequestFlow category={selectedCategory} onClose={() => setSelectedCategory(null)} />}
                {content}
            </Container>
        </Box>
    )
}

export default ServicePros
